import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Loader2,
  Package,
  User,
  Phone,
  MapPin,
  CreditCard,
  RefreshCw,
  AlertCircle,
  Minus,
  Plus,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { format } from "date-fns";
import toast from "react-hot-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Separator } from "../ui/separator";
import api from "../../lib/api";

interface OrderDetailsModalProps {
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
  /** "default" shows all tabs; "refund" shows only Items + Payment */
  variant?: "default" | "refund";
}

interface OrderItem {
  productId: number;
  name: string;
  thumbnailUrl: string;
  price: number;
  quantity: number;
  totalPrice: number;
  mbCode: string;
  status: string;
}

interface ItemAdjustment {
  itemId: string;
  itemName: string;
  type: "rejected" | "added" | "price_change";
  originalQuantity: number;
  adjustedQuantity: number;
  originalPrice: number;
  adjustedPrice: number;
  reason: string;
}

interface Adjustments {
  source: string;
  timestamp: string;
  items: ItemAdjustment[];
  billedTotal: number;
  amountPaid: number;
  refundDue: number;
  additionalDue: number;
}

interface OrderDetails {
  id: string;
  trackingId: string;
  createdAt: string;
  status: string;
  expectedDelivery: string;
  items: OrderItem[];
  address: {
    id: string;
    city: string;
    name: string;
    type: string;
    state: string;
    country: string;
    pincode: string;
    landmark?: string;
    addressLine1: string;
    addressLine2?: string;
    contactNumber: string;
    latitude?: string;
    longitude?: string;
  };
  payment: {
    method: string;
    id: string;
    status: string;
    paidAmount: number;
  };
  prescription?: {
    id: string;
    image_urls: string[];
    status: string;
    doctor_name?: string;
    doctor_contact?: string;
    clinic_name?: string;
    prescription_date?: string;
    valid_until?: string;
    note?: string;
  };
  summary: {
    subtotal: number;
    discount: number;
    deliveryCharge: number;
    handlingCharge: number;
    platformFee: number;
    tax: number;
    total: number;
    savings: number;
  };
  trackingHistory: Array<{
    status: string;
    timestamp: string;
    description: string;
  }>;
  pharmacy: {
    name: string;
    address: {
      map: string;
      city: string;
      line1: string;
      line2: string;
      state: string;
      country: string;
      pincode: string;
      latitude: string;
      longitude: string;
    };
    contact: {
      email: string;
      phone: string;
    };
  };
  adjustments?: Adjustments | null;
  amountPaid?: number;
}

// Per-item refund state shape
interface RefundItemState {
  selected: boolean;
  quantity: number;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  orderId,
  isOpen,
  onClose,
  variant = "default",
}) => {
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundAmount, setRefundAmount] = useState<string>("");
  const [refundReason, setRefundReason] = useState<string>("");
  const [isProcessingRefund, setIsProcessingRefund] = useState(false);
  const [existingRefunds, setExistingRefunds] = useState<any[]>([]);
  // Per-item selection map for Delivered/Dispatched partial refunds
  const [selectedRefundItems, setSelectedRefundItems] = useState<Record<number, RefundItemState>>({});
  const [razorpaySummary, setRazorpaySummary] = useState<{
    amountCaptured: number;
    amountRefunded: number;
    maxRefundable: number;
  } | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    if (isOpen && orderId) {
      fetchOrderDetails();
    }
  }, [isOpen, orderId]);

  // Helper: is the current order a Delivered/Dispatched partial-refund order?
  const isPartialRefundOrder = useMemo(() => {
    return ["DELIVERED", "DISPATCHED"].includes(orderDetails?.status || "");
  }, [orderDetails?.status]);

  // Auto-compute refund amount from selected items (for partial refund orders).
  const computedItemsAmount = useMemo(() => {
    if (!isPartialRefundOrder || !orderDetails) return 0;
    return Object.entries(selectedRefundItems)
      .filter(([, v]) => v.selected)
      .reduce((sum, [productId, v]) => {
        const item = orderDetails.items.find(
          (i) => i.productId === Number(productId),
        );
        return sum + (item ? item.price * v.quantity : 0);
      }, 0);
  }, [selectedRefundItems, isPartialRefundOrder, orderDetails]);

  // Sync refundAmount with computed items total whenever selection changes
  // (only for partial refund orders and only if user hasn't manually overridden below computed)
  useEffect(() => {
    if (!isPartialRefundOrder) return;
    setRefundAmount(computedItemsAmount > 0 ? computedItemsAmount.toFixed(2) : "");
  }, [computedItemsAmount, isPartialRefundOrder]);

  // Once the Razorpay summary arrives after opening the refund dialog,
  // prefill the amount with the best suggestion capped at Razorpay's
  // actual maxRefundable — only for non-partial-refund orders.
  useEffect(() => {
    if (!showRefundDialog || !razorpaySummary) return;
    if (isPartialRefundOrder) return; // amount driven by item selection
    if (refundAmount) return; // user already typed something
    const adj = orderDetails?.adjustments;
    const isCancelled = ["CANCELLED", "PRESCRIPTION_REJECTED"].includes(
      orderDetails?.status || "",
    );
    let suggested = 0;
    if (adj?.refundDue && adj.refundDue > 0) {
      suggested = adj.refundDue;
    } else if (isCancelled) {
      suggested = razorpaySummary.maxRefundable;
    } else {
      suggested = razorpaySummary.maxRefundable;
    }
    const capped = Math.min(suggested, razorpaySummary.maxRefundable);
    if (capped > 0) {
      setRefundAmount(capped.toFixed(2));
    }
  }, [razorpaySummary, showRefundDialog, isPartialRefundOrder]);

  const fetchRazorpaySummary = async (paymentId: string) => {
    console.log("[Refund] Fetching Razorpay payment summary for", paymentId);
    setLoadingSummary(true);
    try {
      const res = await api.getRazorpayPaymentSummary(paymentId);
      console.log("[Refund] Razorpay summary response:", res);
      if (res?.success && res.data) {
        setRazorpaySummary({
          amountCaptured: Number(res.data.amountCaptured || 0),
          amountRefunded: Number(res.data.amountRefunded || 0),
          maxRefundable: Number(res.data.maxRefundable || 0),
        });
      } else {
        setRazorpaySummary(null);
        if (res?.error) {
          toast.error(`Could not fetch payment details: ${res.error}`);
        }
      }
    } catch (error: any) {
      console.error("[Refund] Error fetching Razorpay summary:", error);
      setRazorpaySummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  const fetchExistingRefunds = async (trackingIdOverride?: string) => {
    try {
      // NB: backend `/refunds/order/:orderId` route looks up by tracking_id.
      // Pass trackingIdOverride when calling from fetchOrderDetails() before
      // state has flushed.
      const lookupId =
        trackingIdOverride || orderDetails?.trackingId || orderId;
      const data = await api.getRefundsByOrder(lookupId);
      if (data?.success) {
        setExistingRefunds(data.data || []);
      } else if (data) {
        console.warn("[Refund] getRefundsByOrder non-success:", data);
      }
    } catch (error) {
      console.error("[Refund] Error fetching existing refunds:", error);
    }
  };

  const handleInitiateRefund = async () => {
    console.log("[Refund] handleInitiateRefund triggered", {
      refundAmount,
      refundReason,
      orderDetails,
    });

    // For Delivered/Dispatched orders, validate at least one item is selected
    if (isPartialRefundOrder) {
      const hasSelection = Object.values(selectedRefundItems).some(
        (v) => v.selected,
      );
      if (!hasSelection) {
        toast.error("Please select at least one item to refund");
        return;
      }
    }

    // Validate reason
    if (!refundReason.trim()) {
      const msg = "Please provide a reason for refund";
      console.warn("[Refund] validation failed:", msg);
      toast.error(msg);
      return;
    }

    // Validate payment id
    if (!orderDetails?.payment?.id) {
      const msg = "Payment ID not found for this order — cannot refund";
      console.warn("[Refund] validation failed:", msg, orderDetails?.payment);
      toast.error(msg);
      return;
    }

    // Validate refund amount
    const refundAmountNum = Number.parseFloat(refundAmount);
    if (!Number.isFinite(refundAmountNum) || refundAmountNum <= 0) {
      const msg = "Please enter a valid refund amount";
      console.warn("[Refund] validation failed:", msg, { refundAmount });
      toast.error(msg);
      return;
    }

    // Validate against ground-truth max refundable from Razorpay (preferred).
    // Fall back to local estimate if Razorpay summary isn't loaded yet.
    const paidAmount = razorpaySummary
      ? razorpaySummary.amountCaptured
      : Number(
          orderDetails.amountPaid || orderDetails.payment.paidAmount || 0,
        );
    const alreadyRefunded = razorpaySummary
      ? razorpaySummary.amountRefunded
      : existingRefunds
          .filter((r) => r.status !== "failed" && r.status !== "FAILED")
          .reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const maxRefundable = razorpaySummary
      ? razorpaySummary.maxRefundable
      : Math.max(0, Number((paidAmount - alreadyRefunded).toFixed(2)));

    if (maxRefundable <= 0) {
      const msg =
        "Nothing left to refund — this order has already been fully refunded.";
      console.warn("[Refund] validation failed:", msg, {
        paidAmount,
        alreadyRefunded,
      });
      toast.error(msg);
      return;
    }

    if (refundAmountNum > maxRefundable) {
      const msg = `Refund amount (₹${refundAmountNum.toFixed(
        2,
      )}) exceeds max refundable (₹${maxRefundable.toFixed(
        2,
      )}). Paid: ₹${paidAmount.toFixed(
        2,
      )}, already refunded: ₹${alreadyRefunded.toFixed(2)}.`;
      console.warn("[Refund] validation failed:", msg);
      toast.error(msg);
      return;
    }

    setIsProcessingRefund(true);
    try {
      // Build selected items detail for partial refund audit trail
      const selectedItemsDetail = isPartialRefundOrder
        ? Object.entries(selectedRefundItems)
            .filter(([, v]) => v.selected)
            .map(([productId, v]) => {
              const item = orderDetails.items.find(
                (i) => i.productId === Number(productId),
              );
              return item
                ? {
                    productId: item.productId,
                    name: item.name,
                    mbCode: item.mbCode,
                    refundQty: v.quantity,
                    originalQty: item.quantity,
                    unitPrice: item.price,
                    subtotal: +(item.price * v.quantity).toFixed(2),
                  }
                : null;
            })
            .filter(Boolean)
        : undefined;

      const payload = {
        orderId: orderDetails.id,
        paymentId: orderDetails.payment.id,
        amount: refundAmountNum,
        reason: refundReason.trim(),
        notes: {
          trackingId: orderDetails.trackingId,
          customerName: orderDetails.address?.name,
          ...(isPartialRefundOrder && {
            refundType: "partial_return",
            selectedItems: selectedItemsDetail,
          }),
        },
      };

      console.log("[Refund] POST /refunds/initiate payload:", payload);

      const result = await api.initiateRefund(payload);
      console.log("[Refund] API response:", result);

      if (result?.success) {
        toast.success("Refund initiated successfully");
        setShowRefundDialog(false);
        setRefundAmount("");
        setRefundReason("");
        setSelectedRefundItems({});
        await fetchOrderDetails();
        await fetchExistingRefunds();
      } else {
        const errMsg =
          result?.error ||
          result?.message ||
          "Failed to initiate refund (no error details returned)";
        console.error("[Refund] API returned failure:", result);
        toast.error(errMsg);
      }
    } catch (error: any) {
      console.error("[Refund] Network/exception during refund:", error);
      const errMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to initiate refund — check console for details";
      toast.error(errMsg);
    } finally {
      setIsProcessingRefund(false);
    }
  };

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const response = await api.getB2COrderDetails(orderId);

      if (response.status === "success" && response.data) {
        setOrderDetails(response.data);
        fetchExistingRefunds(response.data?.trackingId);
      } else {
        toast.error("Failed to fetch order details");
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Error fetching order details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm");
    } catch {
      return dateString;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "secondary";
      case "processing":
        return "default";
      case "completed":
        return "default";
      case "cancelled":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const handlePushToSmartPharma = async () => {
    if (!orderDetails?.trackingId) return;

    setLoading(true);
    try {
      const response = await api.pushToSmartPharma(orderDetails.trackingId);
      if (response.success) {
        toast.success("Order pushed to SmartPharma successfully");
      } else {
        toast.error(response.error || "Failed to push order to SmartPharma");
      }
    } catch (error: any) {
      console.error("Error pushing to SmartPharma:", error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || "Error pushing to SmartPharma";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePushToMyrx = async () => {
    if (!orderDetails?.trackingId) return;

    setLoading(true);
    try {
      const response = await api.pushToMyrx(orderDetails.trackingId);
      if (response.success) {
        toast.success("Order pushed to MyRX successfully");
      } else {
        toast.error(response.error || "Failed to push order to MyRX");
      }
    } catch (error: any) {
      console.error("Error pushing to MyRX:", error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || "Error pushing to MyRX";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open && !showRefundDialog) {
      onClose();
    }
  };

  return (
    <>
    <Dialog open={isOpen && !showRefundDialog} onOpenChange={handleDialogClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Order Details</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : orderDetails ? (
          <div className="space-y-6">
            {/* Order Header */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Tracking Number</p>
                <p className="font-semibold">{orderDetails.trackingId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Order Date</p>
                <p className="font-semibold">
                  {formatDate(orderDetails.createdAt)}
                </p>
              </div>
              <div className="flex items-center">
                <Button
                  type="button"
                  size="sm"
                  className="w-full md:w-auto"
                  onClick={handlePushToMyrx}
                  disabled={loading}
                >
                  Push To MyRX
                </Button>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge variant={getStatusBadgeVariant(orderDetails.status)}>
                  {orderDetails.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Status</p>
                <Badge
                  variant={
                    orderDetails.payment.status === "COMPLETED"
                      ? "default"
                      : "secondary"
                  }
                >
                  {orderDetails.payment.status}
                </Badge>
              </div>
              <div className="flex items-center">
                <Button
                  type="button"
                  size="sm"
                  className="w-full md:w-auto"
                  onClick={handlePushToSmartPharma}
                  disabled={loading}
                >
                  Push To SmartPharma
                </Button>
              </div>
            </div>

            <Tabs defaultValue="items" className="w-full">
              <TabsList className={`grid w-full ${variant === "refund" ? "grid-cols-2" : "grid-cols-4"}`}>
                <TabsTrigger value="items">Items</TabsTrigger>
                {variant === "default" && <TabsTrigger value="customer">Customer</TabsTrigger>}
                {variant === "default" && <TabsTrigger value="delivery">Delivery</TabsTrigger>}
                <TabsTrigger value="payment">Payment</TabsTrigger>
              </TabsList>

              {/* Items Tab */}
              <TabsContent value="items" className="space-y-4">
                {/* Adjustment Banner */}
                {orderDetails.adjustments && (
                  <div className={`rounded-lg p-3 text-sm ${
                    orderDetails.adjustments.refundDue > 0
                      ? "bg-red-50 border border-red-200 text-red-800"
                      : orderDetails.adjustments.additionalDue > 0
                      ? "bg-amber-50 border border-amber-200 text-amber-800"
                      : "bg-blue-50 border border-blue-200 text-blue-800"
                  }`}>
                    <div className="flex items-center gap-2 font-medium">
                      <AlertCircle className="w-4 h-4" />
                      Order modified by {orderDetails.adjustments.source}
                    </div>
                    {orderDetails.adjustments.refundDue > 0 && (
                      <p className="mt-1">Refund of ₹{orderDetails.adjustments.refundDue.toFixed(2)} pending</p>
                    )}
                    {orderDetails.adjustments.additionalDue > 0 && (
                      <p className="mt-1">Additional ₹{orderDetails.adjustments.additionalDue.toFixed(2)} due from customer</p>
                    )}
                  </div>
                )}

                <div className="border rounded-lg">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr className="text-black">
                        <th className="text-left p-3">Product</th>
                        <th className="text-center p-3">Status</th>
                        <th className="text-center p-3">Quantity</th>
                        <th className="text-right p-3">Price</th>
                        <th className="text-right p-3">Discount</th>
                        <th className="text-right p-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderDetails.items.map(item => {
                        // Find adjustment info for this item
                        const allAdjItems = orderDetails.adjustments?.items || [];
                        const adj = allAdjItems.find(
                          (a) => a.itemId === item.mbCode || a.itemId === String(item.productId)
                        );
                        const isRejected = item.status === "REJECTED" || adj?.type === "rejected";
                        const isAdded = adj?.type === "added";
                        const isPriceChanged = adj?.type === "price_change";

                        return (
                          <tr key={item.productId} className={`border-t ${isRejected ? "bg-red-50 opacity-70" : isAdded ? "bg-green-50" : ""}`}>
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                {item.thumbnailUrl && (
                                  <img
                                    src={item.thumbnailUrl}
                                    alt={item.name}
                                    className="w-12 h-12 object-cover rounded"
                                  />
                                )}
                                <div>
                                  <p className={`font-medium ${isRejected ? "line-through text-gray-500" : ""}`}>
                                    {item.name}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    Code: {item.mbCode}
                                  </p>
                                  {adj && (
                                    <p className="text-xs text-gray-400 mt-0.5">{adj.reason}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="text-center p-3">
                              {isRejected ? (
                                <Badge variant="destructive" className="text-xs">Rejected</Badge>
                              ) : isAdded ? (
                                <Badge className="text-xs bg-green-600">Added</Badge>
                              ) : isPriceChanged ? (
                                <Badge variant="secondary" className="text-xs border-amber-300 bg-amber-100 text-amber-800">Price Updated</Badge>
                              ) : (
                                <span>{item.status}</span>
                              )}
                            </td>
                            <td className="text-center p-3">
                              {isPriceChanged && adj ? (
                                <span>
                                  <span className="line-through text-gray-400 text-xs mr-1">{adj.originalQuantity}</span>
                                  {adj.adjustedQuantity}
                                </span>
                              ) : (
                                item.quantity
                              )}
                            </td>
                            <td className="text-right p-3">
                              ₹{item.price.toFixed(2)}
                            </td>
                            <td className="text-right p-3">₹0.00</td>
                            <td className="text-right p-3 font-medium">
                              {isPriceChanged && adj ? (
                                <span>
                                  <span className="line-through text-gray-400 text-xs mr-1">₹{adj.originalPrice.toFixed(2)}</span>
                                  <span className="text-amber-700">₹{adj.adjustedPrice.toFixed(2)}</span>
                                </span>
                              ) : isRejected ? (
                                <span className="line-through text-gray-400">₹{item.totalPrice.toFixed(2)}</span>
                              ) : (
                                <span>₹{item.totalPrice.toFixed(2)}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pricing Summary */}
                <div className="border rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>₹{orderDetails.summary.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-₹{orderDetails.summary.discount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Delivery Charges</span>
                    <span>
                      ₹{orderDetails.summary.deliveryCharge.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Handling Charge</span>
                    <span>
                      ₹{orderDetails.summary.handlingCharge.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Platform Fee</span>
                    <span>₹{orderDetails.summary.platformFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tax</span>
                    <span>₹{orderDetails.summary.tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total</span>
                    <span>₹{orderDetails.summary.total.toFixed(2)}</span>
                  </div>
                  {/* Adjustment summary */}
                  {orderDetails.adjustments && (
                    <>
                      <Separator />
                      <div className="flex justify-between text-sm">
                        <span>Amount Paid</span>
                        <span className="font-medium">₹{Number(orderDetails.amountPaid || orderDetails.payment.paidAmount).toFixed(2)}</span>
                      </div>
                      {orderDetails.adjustments.refundDue > 0 && (
                        <div className="flex justify-between text-sm text-red-600 font-medium">
                          <span>Refund Due</span>
                          <span>₹{orderDetails.adjustments.refundDue.toFixed(2)}</span>
                        </div>
                      )}
                      {orderDetails.adjustments.additionalDue > 0 && (
                        <div className="flex justify-between text-sm text-amber-600 font-medium">
                          <span>Additional Due</span>
                          <span>₹{orderDetails.adjustments.additionalDue.toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </TabsContent>

              {/* Customer Tab */}
              <TabsContent value="customer" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Contact Information
                    </h3>
                    <div className="space-y-2">
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        {orderDetails.address.contactNumber}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Delivery Address ({orderDetails.address.name})
                    </h3>
                    <div className="text-sm">
                      <p>{orderDetails.address.addressLine1}</p>
                      {orderDetails.address.addressLine2 && (
                        <p>{orderDetails.address.addressLine2}</p>
                      )}
                      {orderDetails.address.landmark && (
                        <p className="text-gray-600">
                          Landmark: {orderDetails.address.landmark}
                        </p>
                      )}
                      <p>
                        {orderDetails.address.city},{" "}
                        {orderDetails.address.state}
                      </p>
                      <p>{orderDetails.address.pincode}</p>
                    </div>
                  </div>
                </div>

                {orderDetails.prescription && (
                  <div className="space-y-2">
                    <h3 className="font-semibold">Prescription</h3>
                    <div className="space-y-2">
                      <Badge variant="outline">
                        Status: {orderDetails.prescription.status}
                      </Badge>
                      {orderDetails.prescription.doctor_name && (
                        <p>
                          <span className="text-gray-500">Doctor:</span>{" "}
                          {orderDetails.prescription.doctor_name}
                        </p>
                      )}
                      {orderDetails.prescription.note && (
                        <p className="text-sm text-gray-600">
                          Note: {orderDetails.prescription.note}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <h3 className="font-semibold">Pharmacy</h3>
                  <div className="text-sm">
                    <p className="font-medium">{orderDetails.pharmacy.name}</p>
                    <p className="text-gray-600">
                      {orderDetails.pharmacy.address.city},{" "}
                      {orderDetails.pharmacy.address.state}
                    </p>
                    {orderDetails.pharmacy.contact.email && (
                      <p className="text-gray-600">
                        {orderDetails.pharmacy.contact.email}
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Delivery Tab */}
              <TabsContent value="delivery" className="space-y-4">
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Delivery Information
                  </h3>

                  <div className="space-y-2">
                    <p>
                      <span className="text-gray-500">Expected Delivery:</span>{" "}
                      {formatDate(orderDetails.expectedDelivery)}
                    </p>
                    <p>
                      <span className="text-gray-500">Tracking ID:</span>{" "}
                      {orderDetails.trackingId}
                    </p>
                  </div>

                  <div className="mt-4">
                    <h3 className="font-semibold mb-2">Tracking History</h3>
                    <div className="space-y-3">
                      {orderDetails.trackingHistory.map((history, idx) => (
                        <div
                          key={history.timestamp}
                          className="border-l-2 border-gray-200 pl-4"
                        >
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {history.status}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {formatDate(history.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">
                            {history.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <h3 className="font-semibold mb-2">Delivery Address</h3>
                    <div className="text-sm">
                      <p className="font-medium">{orderDetails.address.name}</p>
                      <p>{orderDetails.address.addressLine1}</p>
                      {orderDetails.address.addressLine2 && (
                        <p>{orderDetails.address.addressLine2}</p>
                      )}
                      <p>
                        {orderDetails.address.city},{" "}
                        {orderDetails.address.state}
                      </p>
                      <p>{orderDetails.address.pincode}</p>
                      <p className="mt-2">
                        <span className="text-gray-500">Contact:</span>{" "}
                        {orderDetails.address.contactNumber}
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Payment Tab */}
              <TabsContent value="payment" className="space-y-4">
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Payment Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Payment Method</p>
                      <p className="font-medium">
                        {orderDetails.payment.method}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Payment Status</p>
                      <Badge
                        variant={
                          orderDetails.payment.status === "COMPLETED"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {orderDetails.payment.status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Paid Amount</p>
                      <p className="font-medium">
                        ₹{orderDetails.payment.paidAmount.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>₹{orderDetails.summary.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span>-₹{orderDetails.summary.discount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Delivery Charges</span>
                      <span>
                        ₹{orderDetails.summary.deliveryCharge.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Handling Charge</span>
                      <span>
                        ₹{orderDetails.summary.handlingCharge.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Platform Fee</span>
                      <span>
                        ₹{orderDetails.summary.platformFee.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tax</span>
                      <span>₹{orderDetails.summary.tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total Amount</span>
                      <span>₹{orderDetails.summary.total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Refund Section */}
                  {orderDetails.payment.id &&
                    orderDetails.payment.status === "COMPLETED" && (
                      <div className="mt-6 space-y-4">
                        <Separator />
                        <div>
                          <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <RefreshCw className="w-4 h-4" />
                            Refund Management
                          </h3>

                          {/* Existing Refunds */}
                          {existingRefunds.length > 0 && (
                            <div className="mb-4 space-y-2">
                              <p className="text-sm font-medium text-gray-600">
                                Refund History
                              </p>
                              {existingRefunds.map(refund => (
                                <div
                                  key={refund.id}
                                  className="border rounded-lg p-3 text-sm"
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-medium">
                                        ₹
                                        {Number.parseFloat(
                                          refund.amount,
                                        ).toFixed(2)}
                                      </p>
                                      <p className="text-gray-500">
                                        {refund.reason}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {format(
                                          new Date(refund.createdAt),
                                          "dd MMM yyyy, HH:mm",
                                        )}
                                      </p>
                                    </div>
                                    <Badge
                                      variant={
                                        refund.status === "processed"
                                          ? "default"
                                          : refund.status === "failed"
                                          ? "destructive"
                                          : "secondary"
                                      }
                                    >
                                      {refund.status}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <Button
                            onClick={async () => {
                              // Reset dialog state
                              setRazorpaySummary(null);
                              setRefundAmount("");
                              setRefundReason("");

                              // For Delivered/Dispatched: initialise item selection
                              // with all items deselected at their original quantity
                              if (
                                ["DELIVERED", "DISPATCHED"].includes(
                                  orderDetails?.status || "",
                                )
                              ) {
                                const initial: Record<number, RefundItemState> = {};
                                orderDetails.items.forEach((item) => {
                                  initial[item.productId] = {
                                    selected: false,
                                    quantity: item.quantity,
                                  };
                                });
                                setSelectedRefundItems(initial);
                              } else {
                                setSelectedRefundItems({});
                              }

                              setShowRefundDialog(true);
                              if (orderDetails?.payment?.id) {
                                await fetchRazorpaySummary(
                                  orderDetails.payment.id,
                                );
                              }
                            }}
                            variant="outline"
                            className="w-full"
                            disabled={!orderDetails?.payment?.id}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Initiate Refund
                          </Button>
                        </div>
                      </div>
                    )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Order details not available
          </div>
        )}
      </DialogContent>
    </Dialog>
      {/* Refund Dialog */}
      <AlertDialog
        open={showRefundDialog}
        onOpenChange={(open) => {
          setShowRefundDialog(open);
          if (!open) {
            setRazorpaySummary(null);
            setRefundAmount("");
            setRefundReason("");
            setSelectedRefundItems({});
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Initiate Refund</AlertDialogTitle>
            {/*
              AlertDialogDescription renders as <p>. We only put a short text
              line inside it. The form controls live OUTSIDE the description,
              as siblings under AlertDialogContent, so that <Input>/<Textarea>
              aren't illegally nested inside a <p> (which would silently break
              input interactivity).
            */}
            <AlertDialogDescription>
              Review the refund amount and reason, then confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {(() => {
            const adj = orderDetails?.adjustments;
            const isCancelled = ["CANCELLED", "PRESCRIPTION_REJECTED"].includes(
              orderDetails?.status || "",
            );
            const hasAdjRefund = !!(adj?.refundDue && adj.refundDue > 0);

            // Prefer ground-truth from Razorpay (fetched live when dialog
            // opens). Fall back to local DB values if Razorpay is unreachable.
            const paidAmount = razorpaySummary
              ? razorpaySummary.amountCaptured
              : Number(
                  orderDetails?.amountPaid ||
                    orderDetails?.payment?.paidAmount ||
                    0,
                );
            const alreadyRefunded = razorpaySummary
              ? razorpaySummary.amountRefunded
              : existingRefunds
                  .filter(
                    (r) => r.status !== "failed" && r.status !== "FAILED",
                  )
                  .reduce((sum, r) => sum + Number(r.amount || 0), 0);
            const maxRefundable = razorpaySummary
              ? razorpaySummary.maxRefundable
              : Math.max(
                  0,
                  Number((paidAmount - alreadyRefunded).toFixed(2)),
                );

            const currentInputNum = refundAmount
              ? Number.parseFloat(refundAmount)
              : 0;
            const exceedsMax =
              currentInputNum > maxRefundable && maxRefundable > 0;

            // Partial refund: at least 1 item selected
            const hasSelectedItems = isPartialRefundOrder
              ? Object.values(selectedRefundItems).some((v) => v.selected)
              : true;

            if (loadingSummary) {
              return (
                <div className="py-8 flex items-center justify-center text-gray-500 text-sm gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Fetching payment details from Razorpay...
                </div>
              );
            }

            return (
              <div className="space-y-4 mt-2">
                {!razorpaySummary && (
                  <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                    Could not verify amount with Razorpay — using local
                    estimates. Refund may still fail if the actual captured
                    amount differs.
                  </div>
                )}

                <div
                  className={`flex items-center gap-2 ${
                    isCancelled
                      ? "text-red-600"
                      : isPartialRefundOrder
                        ? "text-blue-600"
                        : hasAdjRefund
                          ? "text-orange-600"
                          : "text-amber-600"
                  }`}
                >
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">
                    {isCancelled
                      ? "Order cancelled — refund the paid amount."
                      : isPartialRefundOrder
                        ? "Select the items and quantities to include in this refund."
                        : hasAdjRefund
                          ? `Refund due from ${adj?.source} adjustments.`
                          : "This action will process a refund for the order."}
                  </span>
                </div>

                {/* ── Partial Refund UI: item selection + qty control ── */}
                {isPartialRefundOrder && orderDetails ? (
                  <div className="space-y-3">
                    <div className="border rounded-lg divide-y overflow-hidden">
                      {orderDetails.items
                        .filter((item) => item.status !== "REJECTED")
                        .map((item) => {
                          const itemState = selectedRefundItems[item.productId] ?? {
                            selected: false,
                            quantity: item.quantity,
                          };
                          const isSelected = itemState.selected;

                          return (
                            <div
                              key={item.productId}
                              className={`flex items-start gap-3 p-3 transition-colors ${
                                isSelected
                                  ? "bg-blue-50 dark:bg-blue-950/30"
                                  : "bg-white dark:bg-gray-900"
                              }`}
                            >
                              {/* Checkbox */}
                              <Checkbox
                                id={`refund-item-${item.productId}`}
                                checked={isSelected}
                                onCheckedChange={(checked) => {
                                  setSelectedRefundItems((prev) => ({
                                    ...prev,
                                    [item.productId]: {
                                      selected: !!checked,
                                      quantity:
                                        prev[item.productId]?.quantity ??
                                        item.quantity,
                                    },
                                  }));
                                }}
                                disabled={isProcessingRefund}
                                className="mt-1"
                              />

                              {/* Item info */}
                              <label
                                htmlFor={`refund-item-${item.productId}`}
                                className="flex-1 cursor-pointer"
                              >
                                <div className="flex items-center gap-2">
                                  {item.thumbnailUrl && (
                                    <img
                                      src={item.thumbnailUrl}
                                      alt={item.name}
                                      className="w-8 h-8 rounded object-cover flex-shrink-0"
                                    />
                                  )}
                                  <div className="min-w-0">
                                    <p
                                      className={`text-sm font-medium leading-tight ${
                                        isSelected ? "" : "text-gray-500"
                                      }`}
                                    >
                                      {item.name}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      ₹{item.price.toFixed(2)} × ordered qty:{" "}
                                      {item.quantity}
                                    </p>
                                  </div>
                                </div>
                              </label>

                              {/* Quantity stepper — only when selected */}
                              {isSelected && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    disabled={
                                      isProcessingRefund ||
                                      itemState.quantity <= 1
                                    }
                                    onClick={() =>
                                      setSelectedRefundItems((prev) => ({
                                        ...prev,
                                        [item.productId]: {
                                          ...prev[item.productId],
                                          quantity: Math.max(
                                            1,
                                            (prev[item.productId]?.quantity ??
                                              item.quantity) - 1,
                                          ),
                                        },
                                      }))
                                    }
                                  >
                                    <Minus className="w-3 h-3" />
                                  </Button>
                                  <span className="w-6 text-center text-sm font-semibold">
                                    {itemState.quantity}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    disabled={
                                      isProcessingRefund ||
                                      itemState.quantity >= item.quantity
                                    }
                                    onClick={() =>
                                      setSelectedRefundItems((prev) => ({
                                        ...prev,
                                        [item.productId]: {
                                          ...prev[item.productId],
                                          quantity: Math.min(
                                            item.quantity,
                                            (prev[item.productId]?.quantity ??
                                              item.quantity) + 1,
                                          ),
                                        },
                                      }))
                                    }
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                  <span className="text-xs text-gray-400 ml-1">
                                    ₹
                                    {(item.price * itemState.quantity).toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>

                    {!hasSelectedItems && (
                      <p className="text-xs text-red-500">
                        Please select at least one item to refund.
                      </p>
                    )}

                    {/* Computed amount — editable, capped at max refundable */}
                    <div>
                      <label
                        htmlFor="refundAmount"
                        className="text-sm font-medium text-gray-700"
                      >
                        Refund Amount
                        <span className="ml-1 text-xs text-gray-400">
                          (auto-computed from selection)
                        </span>
                      </label>
                      <Input
                        id="refundAmount"
                        type="number"
                        placeholder="Select items above"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        max={Math.min(computedItemsAmount, maxRefundable)}
                        min={0}
                        step="0.01"
                        className="mt-1"
                        disabled={isProcessingRefund || !hasSelectedItems}
                      />
                      <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                        <div>
                          Items subtotal: ₹{computedItemsAmount.toFixed(2)}
                          {" · "}
                          <span className="font-medium">
                            Max refundable: ₹{maxRefundable.toFixed(2)}
                          </span>
                        </div>
                        {exceedsMax && (
                          <div className="text-red-600 font-medium">
                            Entered amount exceeds max refundable.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ── Standard full-refund amount input ── */
                  <div>
                    <label
                      htmlFor="refundAmount"
                      className="text-sm font-medium text-gray-700"
                    >
                      Refund Amount
                    </label>
                    <Input
                      id="refundAmount"
                      type="number"
                      placeholder={`Max refundable: ₹${maxRefundable.toFixed(2)}`}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      max={maxRefundable}
                      min={0}
                      step="0.01"
                      className="mt-1"
                      disabled={isProcessingRefund}
                    />
                    <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                      <div>
                        Paid: ₹{paidAmount.toFixed(2)}
                        {alreadyRefunded > 0 && (
                          <>
                            {" · "}Already refunded: ₹
                            {alreadyRefunded.toFixed(2)}
                          </>
                        )}
                        {" · "}
                        <span className="font-medium">
                          Max refundable: ₹{maxRefundable.toFixed(2)}
                        </span>
                      </div>
                      {hasAdjRefund && (
                        <div>
                          Suggested: ₹{adj!.refundDue.toFixed(2)} (from{" "}
                          {adj!.source} adjustments)
                        </div>
                      )}
                      {exceedsMax && (
                        <div className="text-red-600 font-medium">
                          Entered amount exceeds max refundable.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="refundReason"
                    className="text-sm font-medium text-gray-700"
                  >
                    Reason for Refund{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    id="refundReason"
                    placeholder="Please provide a reason for the refund"
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    rows={3}
                    className="mt-1"
                    disabled={isProcessingRefund}
                  />
                </div>

                <div className="text-sm text-gray-600 border rounded-lg p-3 bg-gray-50">
                  <div className="flex justify-between">
                    <span>Amount Paid</span>
                    <span>₹{paidAmount.toFixed(2)}</span>
                  </div>
                  {alreadyRefunded > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <span>Already Refunded</span>
                      <span>-₹{alreadyRefunded.toFixed(2)}</span>
                    </div>
                  )}
                  {isPartialRefundOrder && computedItemsAmount > 0 && (
                    <div className="flex justify-between text-blue-600">
                      <span>Selected Items Total</span>
                      <span>₹{computedItemsAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-500">
                    <span>Max Refundable</span>
                    <span>₹{maxRefundable.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t pt-1 mt-1">
                    <span>This Refund</span>
                    <span>₹{currentInputNum.toFixed(2)}</span>
                  </div>
                </div>

                {/* expose hasSelectedItems so footer button can use it */}
                <input type="hidden" id="_hasSelectedItems" value={hasSelectedItems ? "1" : "0"} />
              </div>
            );
          })()}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessingRefund}>
              Cancel
            </AlertDialogCancel>
            {/*
              Using a regular Button instead of AlertDialogAction so Radix
              does NOT auto-close the dialog on click. We close it ourselves
              only on success, so backend errors are shown to the user with
              form state preserved.
            */}
            <Button
              type="button"
              onClick={handleInitiateRefund}
              disabled={
                isProcessingRefund ||
                (isPartialRefundOrder &&
                  !Object.values(selectedRefundItems).some((v) => v.selected))
              }
            >
              {isProcessingRefund ? "Processing..." : "Initiate Refund"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OrderDetailsModal;
